import React, { useState, useRef } from 'react';
import { ConfigProvider, Avatar } from 'antd';
import type { MenuProps } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTheme } from '../../../theme/ThemeContext';
import Input from '../../../atoms/Input/Input';
import Dropdown from '../../../atoms/Dropdown/Dropdown';
import Modal from '../../../atoms/Modal/Modal';
import NewAskAsperLogo from '../../../atoms/Icon/NewAskAsperLogo';
import EditDoc from '../../../atoms/Icon/EditDoc';
import Search from '../../../atoms/Icon/Search';
import ClockFastForward from '../../../atoms/Icon/ClockFastForward';
import LayoutLeft from '../../../atoms/Icon/LayoutLeft';
import DotsVertical from '../../../atoms/Icon/DotsVertical';
import Check from '../../../atoms/Icon/Check';
import XClose from '../../../atoms/Icon/XClose';
import { AgenticSidebarProps, ConversationThread } from '../AskAsperAgentic.types';

/**
 * AgenticSidebar - Collapsible sidebar for the AskAsper Agentic Platform
 * Features:
 * - Collapsed and expanded views
 * - New Chat and Search Chats buttons
 * - Thread list with edit/delete functionality
 * - User profile section
 * - Toggle sidebar button
 */
const AgenticSidebar: React.FC<AgenticSidebarProps> = (props) => {
    const {
        threads = [],
        threadsLoading = false,
        activeThreadId,
        user = { name: 'User' },
        collapsed: controlledCollapsed,
        defaultCollapsed = false,
        onNewChat,
        onSearchChats,
        onSearchChatsClear,
        onThreadSelect,
        onThreadEdit,
        onThreadDelete,
        onToggleSidebar,
        onUserClick,
        hideHeader = false,
    } = props;

    const theme = useTheme();
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
    const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [modal, contextHolder] = Modal.useModal();
    const preventThreadSelectRef = useRef(false);

    // Use controlled or uncontrolled collapsed state
    const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;

    const handleToggleSidebar = () => {
        const newCollapsed = !isCollapsed;
        if (controlledCollapsed === undefined) {
            setInternalCollapsed(newCollapsed);
        }
        onToggleSidebar?.(newCollapsed);
    };

    const handleEditClick = (threadId: string, currentName: string) => {
        setEditingThreadId(threadId);
        setEditValue(currentName);
    };

    const handleSaveEdit = (threadId: string) => {
        if (editValue.trim() && onThreadEdit) {
            onThreadEdit(threadId, editValue.trim());
        }
        setEditingThreadId(null);
        setEditValue('');
    };

    const handleCancelEdit = () => {
        setEditingThreadId(null);
        setEditValue('');
    };

    const handleDeleteClick = (threadId: string) => {
        modal.confirm({
            title: 'Do you want to delete this thread?',
            icon: <ExclamationCircleOutlined style={{ color: '#ca3d3d' }} />,
            content: 'Are you sure you want to permanently delete this thread? This action cannot be undone',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            centered: true,
            onOk: () => {
                if (onThreadDelete) {
                    onThreadDelete(threadId);
                }
            },
        });
    };

    const handleMenuAction = (action: () => void) => {
        preventThreadSelectRef.current = true;
        action();
        // Reset after overlay closes to prevent click-through from triggering onThreadSelect
        setTimeout(() => {
            preventThreadSelectRef.current = false;
        }, 100);
    };

    const getMenuItems = (threadId: string, threadName: string): MenuProps['items'] => [
        {
            key: 'edit',
            label: 'Edit',
            onClick: (info) => {
                info.domEvent?.stopPropagation();
                handleMenuAction(() => handleEditClick(threadId, threadName));
            },
        },
        {
            key: 'delete',
            label: 'Delete',
            onClick: (info) => {
                info.domEvent?.stopPropagation();
                handleMenuAction(() => handleDeleteClick(threadId));
            },
        },
    ];

    // Helper function to normalize a date to YYYY-MM-DD format for consistent comparison
    const getDateString = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper function to extract date string (YYYY-MM-DD) from ISO date string
    const getThreadDateString = (dateString: string | undefined | null): string => {
        if (!dateString) return '1970-01-01';
        const dateMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
        if (dateMatch) return dateMatch[1];
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '1970-01-01';
        return getDateString(date);
    };

    const filteredThreads = searchQuery.trim()
        ? threads.filter((t) =>
              t.thread_name.toLowerCase().includes(searchQuery.trim().toLowerCase())
          )
        : threads;

    // Group threads by time buckets (Today, Yesterday, This Week, Older)
    const groupThreadsByTime = () => {
        const now = new Date();
        const todayStr = getDateString(now);
        const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterdayDate = new Date(todayDate);
        yesterdayDate.setDate(yesterdayDate.getDate() - 1);
        const yesterdayStr = getDateString(yesterdayDate);
        const thisWeekDate = new Date(todayDate);
        thisWeekDate.setDate(thisWeekDate.getDate() - 7);
        const thisWeekStr = getDateString(thisWeekDate);

        const grouped: {
            today: ConversationThread[];
            yesterday: ConversationThread[];
            thisWeek: ConversationThread[];
            older: ConversationThread[];
        } = { today: [], yesterday: [], thisWeek: [], older: [] };

        filteredThreads.forEach((thread) => {
            const threadDateStr = getThreadDateString(thread.created_on);
            if (threadDateStr === todayStr) grouped.today.push(thread);
            else if (threadDateStr === yesterdayStr) grouped.yesterday.push(thread);
            else if (threadDateStr >= thisWeekStr) grouped.thisWeek.push(thread);
            else grouped.older.push(thread);
        });
        return grouped;
    };

    const groupedThreads = groupThreadsByTime();

    const truncateText = (text: string, maxLength: number = 24) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const renderThreadItem = (thread: ConversationThread) => {
        const isActive = thread.thread_id === activeThreadId;
        const isEditing = editingThreadId === thread.thread_id;

        if (isEditing) {
            return (
                <div
                    key={thread.thread_id}
                    className="flex items-center gap-2 px-2 py-1 rounded-lg bg-[#f0f0f0]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex-1 min-w-0">
                        <Input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onPressEnter={() => handleSaveEdit(thread.thread_id)}
                            className="h-[28px] text-[14px]"
                            autoFocus
                        />
                    </div>
                    <div className="flex items-center gap-1">
                        <div
                            className="cursor-pointer hover:opacity-70"
                            onClick={() => handleSaveEdit(thread.thread_id)}
                        >
                            <Check size={14} color="#42a531" />
                        </div>
                        <div className="cursor-pointer hover:opacity-70" onClick={handleCancelEdit}>
                            <XClose size={14} color="#ca3d3d" />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div
                key={thread.thread_id}
                className={`
                    group flex items-center justify-between gap-4 px-2 py-1 rounded-lg cursor-pointer transition-colors
                    ${isActive ? 'bg-[#f0f0f0]' : 'hover:bg-[rgba(0,0,0,0.02)]'}
                `}
                onClick={() => {
                    if (preventThreadSelectRef.current) return;
                    onThreadSelect?.(thread.thread_id);
                }}
            >
                <p className="font-sans text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] m-0 truncate flex-1">
                    {truncateText(thread.thread_name)}
                </p>
                <Dropdown
                    menu={{ items: getMenuItems(thread.thread_id, thread.thread_name) }}
                    trigger={['click']}
                    placement="bottomRight"
                >
                    <div
                        className={`cursor-pointer hover:opacity-70 rotate-90 shrink-0 transition-opacity ${
                            isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <DotsVertical size={16} color="rgba(0,0,0,0.45)" />
                    </div>
                </Dropdown>
            </div>
        );
    };

    const renderSection = (title: string, sectionThreads: ConversationThread[]) => {
        if (sectionThreads.length === 0) return null;
        return (
            <div className="flex flex-col gap-1 w-full">
                <div className="px-2 py-0">
                    <p className="font-sans font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.65)] m-0">
                        {title}
                    </p>
                </div>
                <div className="flex flex-col gap-3">{sectionThreads.map(renderThreadItem)}</div>
            </div>
        );
    };

    // Collapsed view - only shows icons
    if (isCollapsed) {
        return (
            <ConfigProvider theme={theme}>
                {contextHolder}
                <div
                    className="bg-white border-r border-[#f0f0f0] h-full flex flex-col w-[72px] transition-all duration-300"
                    data-test={props['data-test']}
                    data-test-id={props['data-test-id']}
                    data-agentic-sidebar
                    data-collapsed="true"
                >
                    {/* Header with Logo only */}
                    <div className="flex items-center justify-center py-6 px-4">
                        <NewAskAsperLogo size={24} />
                    </div>

                    {/* Menu Icons - 40x40 containers with 20px icons */}
                    <div className="flex flex-col gap-3 px-4 items-center">
                        {/* New Chat - with brand background */}
                        <div
                            className="flex items-center justify-center w-10 h-10 rounded-lg cursor-pointer bg-[#f7f5ff] hover:bg-[#efe9ff] transition-colors"
                            onClick={onNewChat}
                            title="New Chat"
                        >
                            <EditDoc size={20} color="#5f49bc" />
                        </div>
                        {/* Recent/History */}
                        <div
                            className="flex items-center justify-center w-10 h-10 rounded-lg cursor-pointer hover:bg-[rgba(0,0,0,0.02)] transition-colors"
                            onClick={onSearchChats}
                            title="Recent Chats"
                        >
                            <ClockFastForward size={20} color="rgba(0,0,0,0.65)" />
                        </div>
                    </div>

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Bottom Section */}
                    <div className="flex flex-col gap-3 items-center px-4">
                        {/* Layout Toggle */}
                        <div
                            className="flex items-center justify-center w-8 h-8 rounded cursor-pointer hover:bg-[rgba(0,0,0,0.02)] transition-colors"
                            onClick={handleToggleSidebar}
                            title="Expand Sidebar"
                        >
                            <LayoutLeft size={16} color="rgba(0,0,0,0.65)" />
                        </div>

                        {/* Divider */}
                        <div className="h-[1px] bg-[#f0f0f0] w-full" />

                        {/* User Profile - Avatar only */}
                        <div className="pb-4">
                            <div
                                className="cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={onUserClick}
                                title={user.name}
                            >
                                <Avatar size={32} src={user.avatar} className="bg-[#fafafa]">
                                    {!user.avatar && user.name.charAt(0).toUpperCase()}
                                </Avatar>
                            </div>
                        </div>
                    </div>
                </div>
            </ConfigProvider>
        );
    }

    // Expanded view
    return (
        <ConfigProvider theme={theme}>
            {contextHolder}
            <div
                className="bg-white border-r border-[#f0f0f0] h-full flex flex-col w-[244px] transition-all duration-300"
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-agentic-sidebar
                data-collapsed="false"
            >
                {/* Header with Logo - hidden when hideHeader is true */}
                {!hideHeader && (
                    <>
                        <div className="flex items-center gap-3 px-6 py-[18px]">
                            <NewAskAsperLogo size={24} />
                            <h4 className="font-sans font-semibold text-[20px] leading-[28px] text-[rgba(0,0,0,0.88)] m-0">
                                AskAsper
                            </h4>
                        </div>

                        {/* Divider */}
                        <div className="h-[1px] bg-[#f0f0f0] w-full" />
                    </>
                )}

                {/* Menu Items - scrollable middle section */}
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-4 p-4">
                    {/* New Chat & Search Chats */}
                    <div className="flex flex-col gap-3">
                        <div
                            className="flex items-center gap-2 px-2 py-1 rounded-lg cursor-pointer hover:bg-[rgba(0,0,0,0.02)] transition-colors"
                            onClick={onNewChat}
                        >
                            <EditDoc size={16} color="rgba(0,0,0,0.88)" />
                            <span className="font-sans text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                                New Chat
                            </span>
                        </div>
                        <Input
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                onSearchChats?.();
                            }}
                            onFocus={() => onSearchChats?.()}
                            placeholder="Search chats"
                            prefix={<Search size={14} color="rgba(0,0,0,0.45)" />}
                            suffix={
                                searchQuery ? (
                                    <div
                                        className="cursor-pointer hover:opacity-70 flex items-center"
                                        onClick={() => {
                                            setSearchQuery('');
                                            onSearchChatsClear?.();
                                        }}
                                    >
                                        <XClose size={12} color="rgba(0,0,0,0.45)" />
                                    </div>
                                ) : null
                            }
                            className="h-[32px] text-[14px]"
                            allowClear={false}
                        />
                    </div>

                    <div className="flex flex-col gap-6">
                        {threadsLoading ? (
                            <>
                                <style>{`
                                    @keyframes agentic-sidebar-threads-loading-sweep {
                                        0% { transform: translateX(-100%); }
                                        100% { transform: translateX(400%); }
                                    }
                                `}</style>
                                <div className="w-full rounded-sm overflow-hidden bg-[#e5e7eb]">
                                    <div
                                        className="h-2 w-1/3 rounded-sm"
                                        style={{
                                            background: 'linear-gradient(to right, #3b82f6, #06b6d4, #22c55e)',
                                            animation: 'agentic-sidebar-threads-loading-sweep 2s ease-in-out infinite',
                                        }}
                                        role="progressbar"
                                        aria-valuenow={undefined}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                        aria-label="Loading threads"
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                {searchQuery.trim() && filteredThreads.length === 0 ? (
                                    <p className="font-sans text-[13px] text-[rgba(0,0,0,0.45)] text-center px-2 py-4 m-0">
                                        No chats found
                                    </p>
                                ) : (
                                    <>
                                        {renderSection('Today', groupedThreads.today)}
                                        {renderSection('Yesterday', groupedThreads.yesterday)}
                                        {renderSection('This Week', groupedThreads.thisWeek)}
                                        {renderSection('Older', groupedThreads.older)}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>


            </div>
        </ConfigProvider>
    );
};

export default AgenticSidebar;
